'use client';

import { createContext, useContext } from 'react';

export type ImagePickerSource = 'photos' | 'prompt';

export interface ImagePickerOptions {
  source?: ImagePickerSource;
}

/**
 * Returns the base64 string (without data URL prefix) of the picked image,
 * or null if the user cancelled. A never-resolving promise indicates
 * cancellation in contexts where callers chain further actions.
 */
export type ImagePickerFn = (
  options?: ImagePickerOptions
) => Promise<string | null>;

/**
 * Default web implementation: opens a native file picker via <input type="file">.
 * Equivalent to Capacitor Camera with webUseInput: true.
 */
const webImagePicker: ImagePickerFn = (_options?) => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.addEventListener('cancel', () => resolve(null));

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split('base64,')[1];
        resolve(base64 ?? null);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    };

    input.click();
  });
};

export const ImagePickerContext = createContext<ImagePickerFn>(webImagePicker);

export const useImagePicker = (): ImagePickerFn =>
  useContext(ImagePickerContext);
